import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  Avatar,
  AvatarGroup,
  LinearProgress,
  Divider,
  IconButton
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Analytics as AnalyticsIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Cloud as CloudIcon,
  Mobile as MobileIcon,
  Language as GlobalIcon,
  AutoAwesome as AIIcon,
  PlayArrow as PlayIcon,
  TrendingUp as TrendingIcon,
  People as PeopleIcon,
  Verified as VerifiedIcon,
  Star as StarIcon,
  ArrowForward as ArrowIcon
} from '@mui/icons-material';

import MobileFriendlyIcon from '@mui/icons-material/MobileFriendly';

const PlatformFeaturesModern = () => {
  const [isInView, setIsInView] = useState(false);
  const [activeMetric, setActiveMetric] = useState(0);
  const sectionRef = useRef(null);

  const platformStats = [
    { label: 'Toplam Modül Sayısı', value: '18+', color: '#3b82f6', progress: 95 },
    { label: 'Widget Çeşidi', value: '50+', color: '#10b981', progress: 92 },
    { label: 'Sayfa Tipi', value: '5', color: '#f59e0b', progress: 100 },
    { label: 'Özelleştirme Oranı', value: '100%', color: '#ef4444', progress: 100 }
  ];

  const coreFeatures = [
    {
      icon: <DashboardIcon sx={{ fontSize: 28 }} />,
      title: '18+ Modül Sistemi',
      description: 'Haber yönetiminden kullanıcı etkileşimine kadar her ihtiyacınızı karşılayan modüller.',
      color: '#3b82f6',
      features: ['Haber & Makale Yönetimi', 'Forum & Blog Modülü', 'Duyuru & İlan Servisi', 'Kullanıcı & Abonelik']
    },
    {
      icon: <AnalyticsIcon sx={{ fontSize: 28 }} />,
      title: '5 Farklı Sayfa Tipi',
      description: 'Ana sayfa, kategori, haber detay, profil ve özel sayfalar için tasarım özgürlüğü.',
      color: '#10b981',
      features: ['Sınırsız Sayfa Tasarımı', 'Widget Tabanlı Yapı', 'Drag & Drop Editor', 'Responsive Tasarım']
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 28 }} />,
      title: 'Header & Footer Özelleştirme',
      description: 'Sitenizin kimliğini yansıtan tamamen özelleştirilebilir üst ve alt bölümler.',
      color: '#ef4444',
      features: ['Özel Logo & Menü', 'Sosyal Medya Entegrasyonu', 'Reklam Alanları', 'İletişim Bilgileri']
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 28 }} />,
      title: 'Zengin İçerik Servisleri',
      description: 'Hava durumu, borsa, kripto para, spor skorları ile içeriklerinizi zenginleştirin.',
      color: '#f59e0b',
      features: ['Canlı Hava Durumu', 'Borsa & Kripto Verileri', 'Spor Skorları', 'Döviz Kurları']
    },
    {
      icon: <CloudIcon sx={{ fontSize: 28 }} />,
      title: 'Abonelik & Mail Yönetimi',
      description: 'Profesyonel e-bülten sistemi ve abonelik yönetimi ile okuyucularınızla bağlantıda kalın.',
      color: '#8b5cf6',
      features: ['E-bülten Sistemi', 'Abonelik Paketleri', 'Mail Template\'leri', 'Otomatik Gönderim']
    },
    {
      icon: <MobileFriendlyIcon sx={{ fontSize: 28 }} />,
      title: 'Teknik Altyapı',
      description: 'AMP, CDN, SEO optimizasyonu ve diğer profesyonel haber sitesi standartları.',
      color: '#06b6d4',
      features: ['AMP Desteği', 'Global CDN', 'SEO Optimizasyonu', 'Hız Optimizasyonu']
    }
  ];

  const integrations = [
    { name: 'Reuters API', logo: 'https://www.reuters.com/favicon.ico', status: 'active' },
    { name: 'AP News Wire', logo: 'https://apnews.com/favicon.ico', status: 'active' },
    { name: 'AA Ajansı', logo: 'https://www.aa.com.tr/favicon.ico', status: 'active' },
    { name: 'Google News', logo: 'https://news.google.com/favicon.ico', status: 'active' },
    { name: 'TRT Haber', logo: 'https://www.trthaber.com/favicon.ico', status: 'coming' },
    { name: 'Fact-Check.org', logo: 'https://www.factcheck.org/favicon.ico', status: 'coming' }
  ];

  const testimonialAvatars = [
    'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
    'https://images.unsplash.com/photo-1494790108755-2616b612b1e5?w=100&h=100&fit=crop&crop=face',
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
    'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face'
  ];

  // Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Auto-rotate metrics
  useEffect(() => {
    if (!isInView) return;

    const interval = setInterval(() => {
      setActiveMetric((prev) => (prev + 1) % platformStats.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isInView, platformStats.length]);

  const currentMetric = platformStats[activeMetric];

  return (
    <Box
      ref={sectionRef}
      sx={{
        py: { xs: 8, md: 12 },
        background: `
          linear-gradient(135deg, #1e293b 0%, #0f172a 50%, #1e293b 100%)
        `,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 25% 25%, ${currentMetric?.color}08 0%, transparent 50%),
            radial-gradient(circle at 75% 75%, ${currentMetric?.color}05 0%, transparent 50%)
          `,
          transition: 'all 1s ease',
          pointerEvents: 'none'
        }
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        {/* Header */}
        <Box textAlign="center" mb={8}>
          <Chip
            label="PLATFORM"
            sx={{
              background: `linear-gradient(135deg, ${currentMetric?.color}20, ${currentMetric?.color}40)`,
              color: currentMetric?.color,
              border: `1px solid ${currentMetric?.color}30`,
              backdropFilter: 'blur(10px)',
              fontSize: '0.9rem',
              fontWeight: 600,
              mb: 4,
              animation: isInView ? 'fadeInUp 0.6s ease-out' : 'none'
            }}
          />
          
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2.8rem', md: '4rem' },
              fontWeight: 900,
              mb: 3,
              color: 'white',
              background: `linear-gradient(135deg, #ffffff 0%, ${currentMetric?.color} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: isInView ? 'fadeInUp 0.8s ease-out 0.2s both' : 'none'
            }}
          >
            Güçlü Platform
            <br />
            <span style={{ color: currentMetric?.color }}>Sınırsız İmkanlar</span>
          </Typography>
          
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: 1.6,
              animation: isInView ? 'fadeInUp 1s ease-out 0.4s both' : 'none'
            }}
          >
            Profesyonel haber sitesi ihtiyaçlarınız için 
            tasarlanmış kapsamlı çözümler
          </Typography>
        </Box>

        {/* Platform Stats */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 3,
            mb: 10,
            animation: isInView ? 'fadeInUp 1.2s ease-out 0.6s both' : 'none'
          }}
        >
          {platformStats.map((stat, index) => (
            <Card
              key={index}
              onClick={() => setActiveMetric(index)}
              sx={{
                background: activeMetric === index
                  ? `linear-gradient(135deg, ${stat.color}15, ${stat.color}08)`
                  : 'rgba(255, 255, 255, 0.03)',
                backdropFilter: 'blur(20px)',
                border: activeMetric === index
                  ? `2px solid ${stat.color}50`
                  : '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '16px',
                p: 3,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                transform: activeMetric === index ? 'translateY(-4px)' : 'translateY(0)',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  border: `2px solid ${stat.color}40`
                }
              }}
            >
              <CardContent sx={{ p: 0, textAlign: 'center' }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 900,
                    color: stat.color,
                    mb: 1,
                    fontSize: { xs: '1.8rem', md: '2.2rem' }
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontWeight: 600,
                    mb: 2,
                    fontSize: '0.9rem'
                  }}
                >
                  {stat.label}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={stat.progress}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: stat.color,
                      borderRadius: 3
                    }
                  }}
                />
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Core Features Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            gap: 4,
            mb: 10
          }}
        >
          {coreFeatures.map((feature, index) => (
            <Card
              key={index}
              sx={{
                background: 'rgba(255, 255, 255, 0.03)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '20px',
                p: 3,
                height: '100%',
                transition: 'all 0.3s ease',
                animation: isInView ? `slideInUp 0.8s ease-out ${index * 0.1}s both` : 'none',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  border: `1px solid ${feature.color}40`,
                  background: `linear-gradient(135deg, ${feature.color}08, rgba(255, 255, 255, 0.03))`
                }
              }}
            >
              <CardContent sx={{ p: 0 }}>
                {/* Icon */}
                <Box
                  sx={{
                    width: 70,
                    height: 70,
                    borderRadius: '16px',
                    background: `${feature.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: feature.color,
                    mb: 3
                  }}
                >
                  {feature.icon}
                </Box>

                {/* Title & Description */}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: 'white',
                    mb: 2
                  }}
                >
                  {feature.title}
                </Typography>
                
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    lineHeight: 1.6,
                    mb: 3
                  }}
                >
                  {feature.description}
                </Typography>

                {/* Feature List */}
                <Stack spacing={1}>
                  {feature.features.map((item, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Box
                        sx={{
                          width: 4,
                          height: 4,
                          borderRadius: '50%',
                          background: feature.color,
                          flexShrink: 0
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.8)',
                          fontSize: '0.85rem'
                        }}
                      >
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Integrations Section */}
        <Box mb={10}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: 'white',
              textAlign: 'center',
              mb: 6
            }}
          >
            Haber Ajansları & Entegrasyonlar
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
              gap: 3
            }}
          >
            {integrations.map((integration, index) => (
              <Card
                key={index}
                sx={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  p: 2,
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    background: 'rgba(255, 255, 255, 0.08)'
                  }
                }}
              >
                {integration.status === 'active' && (
                  <Chip
                    size="small"
                    label="Aktif"
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      background: '#10b981',
                      color: 'white',
                      fontSize: '0.7rem',
                      height: '20px'
                    }}
                  />
                )}
                
                <Avatar
                  src={integration.logo}
                  sx={{
                    width: 40,
                    height: 40,
                    mx: 'auto',
                    mb: 1,
                    background: 'rgba(255, 255, 255, 0.1)'
                  }}
                />
                
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '0.8rem',
                    fontWeight: 500
                  }}
                >
                  {integration.name}
                </Typography>
              </Card>
            ))}
          </Box>
        </Box>


        {/* Bottom CTA */}
        <Box textAlign="center">
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              mb: 3,
              color: 'white'
            }}
          >
            Haber odanızı dijitalleştirmeye hazır mısınız? 📰
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            justifyContent="center"
            alignItems="center"
          >
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowIcon />}
              sx={{
                background: `linear-gradient(135deg, ${currentMetric?.color} 0%, ${currentMetric?.color}dd 100%)`,
                color: 'white',
                px: 6,
                py: 2.5,
                borderRadius: '50px',
                fontSize: '1.1rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: `0 10px 30px ${currentMetric?.color}40`,
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: `0 15px 40px ${currentMetric?.color}50`
                }
              }}
            >
              Haber Odası Demo
            </Button>
            
            <Button
              variant="outlined"
              size="large"
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                px: 6,
                py: 2.5,
                borderRadius: '50px',
                fontSize: '1.1rem',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  borderColor: currentMetric?.color,
                  background: `${currentMetric?.color}15`
                }
              }}
            >
              Editör Paneli
            </Button>
          </Stack>
        </Box>
      </Container>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(40px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </Box>
  );
};

export default PlatformFeaturesModern;
